import type { PurchaseLink } from '../types/purchasing';

export interface PurchaseLinkDraft {
  key: string;
  label: string;
  url: string;
}

export function emptyPurchaseLinkDraft(): PurchaseLinkDraft {
  return {
    key: `link-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    label: '',
    url: '',
  };
}

export function normalizePurchaseUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(withScheme);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    if (!parsed.hostname.includes('.')) return null;
    return parsed.href;
  } catch {
    return null;
  }
}

const URL_IN_TEXT = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;

export interface PurchaseTextPart {
  text: string;
  href?: string;
}

function trimUrlTrail(token: string): { url: string; trail: string } {
  let url = token;
  let trail = '';
  while (url.length > 1 && /[.,;:!?]$/.test(url)) {
    trail = url.slice(-1) + trail;
    url = url.slice(0, -1);
  }
  while (url.endsWith(')') && (url.match(/\(/g) || []).length < (url.match(/\)/g) || []).length) {
    trail = ')' + trail;
    url = url.slice(0, -1);
  }
  return { url, trail };
}

/** Split justification, line text, and notes so http(s) URLs can render as anchors. */
export function linkifyPurchaseText(raw: string): PurchaseTextPart[] {
  const text = raw || '';
  const parts: PurchaseTextPart[] = [];
  let last = 0;
  for (const match of Array.from(text.matchAll(URL_IN_TEXT))) {
    const start = match.index ?? 0;
    if (start > last) parts.push({ text: text.slice(last, start) });
    const { url, trail } = trimUrlTrail(match[0]);
    const href = normalizePurchaseUrl(url);
    if (href) parts.push({ text: url, href });
    else parts.push({ text: url });
    if (trail) parts.push({ text: trail });
    last = start + match[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  if (!parts.length) parts.push({ text });
  return parts;
}

function purchaseUrlKey(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const normalized = normalizePurchaseUrl(trimmed) || trimmed;
  return normalized.replace(/\/$/, '').toLowerCase();
}

/** Links that are not already the product URL on a line item. */
export function additionalPurchaseLinks<T extends { url?: string | null }>(
  links: T[] | undefined,
  lineItems: Array<{ productUrl?: string | null }> | undefined,
): T[] {
  const onLines = new Set(
    (lineItems || []).map(line => purchaseUrlKey(line.productUrl || '')).filter(Boolean),
  );
  return (links || []).filter(link => {
    const key = purchaseUrlKey(link.url || '');
    return !!key && !onLines.has(key);
  });
}

export function purchaseLinkLabel(link: Pick<PurchaseLink, 'url' | 'label'>): string {
  const label = link.label?.trim();
  if (label) return label;
  try {
    return new URL(link.url).hostname.replace(/^www\./i, '');
  } catch {
    return link.url;
  }
}

export function parsePurchaseLinks(raw: unknown): PurchaseLink[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item, index): PurchaseLink | null => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const url = typeof row.url === 'string' ? normalizePurchaseUrl(row.url) : null;
      if (!url) return null;
      const label = typeof row.label === 'string' ? row.label.trim() : '';
      return {
        id: typeof row.id === 'string' && row.id.trim() ? row.id.trim() : `link-${index + 1}`,
        url,
        ...(label ? { label } : {}),
      };
    })
    .filter((item): item is PurchaseLink => !!item);
}

export function draftsToLinks(drafts: PurchaseLinkDraft[]): {
  links: PurchaseLink[];
  invalidCount: number;
} {
  let invalidCount = 0;
  const links = drafts
    .map((draft, index): PurchaseLink | null => {
      const urlRaw = draft.url.trim();
      const label = draft.label.trim();
      if (!urlRaw && !label) return null;
      const url = normalizePurchaseUrl(urlRaw);
      if (!url) {
        invalidCount += 1;
        return null;
      }
      return {
        id: draft.key || `link-${index + 1}`,
        url,
        ...(label ? { label } : {}),
      };
    })
    .filter((item): item is PurchaseLink => !!item);
  return { links, invalidCount };
}

export function preparedPurchaseLinks(drafts: PurchaseLinkDraft[]): PurchaseLink[] {
  const { links, invalidCount } = draftsToLinks(drafts);
  if (invalidCount) throw new Error('Fix or remove invalid links.');
  return links;
}

export function linksToDrafts(links: PurchaseLink[]): PurchaseLinkDraft[] {
  return links.map(link => ({
    key: link.id,
    label: link.label || '',
    url: link.url,
  }));
}

export function serializePurchaseLinks(links: PurchaseLink[]) {
  return links.map(link => ({
    id: link.id,
    url: link.url,
    ...(link.label ? { label: link.label } : {}),
  }));
}
